import React, { useContext, useState } from "react";
import { WidgetInput } from "react-addressfinder";
import { useSelector } from "react-redux";
import MultiStepFormContext from "./../MultiStepForm/MultiStepFormContext";
import "./allaccountsteps.scss";
import {
  bankDetls,
  createPricingPlan,
  createReffPartner,
  customerTab,
  ownerTab,
  pagination
} from "../../locales/en";
import { getUserCountryCode, sanitizeInput } from "../../utils/utils";
import {
  ADDRESSFINDERKEY,
  COUNTRY_CODE_AU,
  COUNTRY_CODE_NZ,
  MYOB,
  NUMBER_REGEX,
  QUICKBOOKS,
  RECKON,
  XERO
} from "../../utils/constants/appConstants";
import {
  companyEntityOptions,
  getFindFromOptions,
  getOwnPropertyOptions
} from "../../utils/getDropdownOptions";

const BankDetails = () => {
  const {
    bankDetails,
    setBankDetails,
    bankName,
    connectedSoftName,
    handleUpdateOnboarding,
    next,
    prev
  } = useContext(MultiStepFormContext);

  const initialErrors = {
    businessName: "",
    companyEntity: "",
    bankAccountType: "",
    bankAccount: "",
    confirmAccount: "",
    accountantFirm: "",
    accountantName: "",
    property: "",
    propertyAddress: "",
    findFrom: "",
    specify: ""
  };

  const country = getUserCountryCode();

  const [errors, setErrors] = useState(initialErrors);

  const handleSubmit = () => {
    let isValid = true;
    const newErrors = { ...errors };
    const hasBankConnected = bankName && bankName !== "";
    const hasDropdown = !!bankDetails.bankAccountType;
    const hasManual = !!bankDetails.bankAccount;

    if (!bankDetails.businessName) {
      newErrors.businessName = bankDetls?.errMsg.item1;
      isValid = false;
    } else {
      newErrors.businessName = "";
    }

    if (!bankDetails.companyEntity) {
      newErrors.companyEntity = bankDetls?.errMsg.item3;
      isValid = false;
    } else {
      newErrors.companyEntity = "";
    }

    if (!hasBankConnected) {
      if (!hasManual) {
        newErrors.bankAccount = bankDetls?.errMsg.item4;
        isValid = false;
      } else if (!NUMBER_REGEX.test(bankDetails.bankAccount)) {
        newErrors.bankAccount = bankDetls?.errMsg.item5;
        isValid = false;
      } else {
        newErrors.bankAccount = "";
      }
    } else {
      if (!hasDropdown && !hasManual) {
        newErrors.bankAccountType = bankDetls?.errMsg.item13;
        newErrors.bankAccount = "";
        isValid = false;
      } else {
        newErrors.bankAccountType = "";
      }

      if (hasManual) {
        if (!NUMBER_REGEX.test(bankDetails.bankAccount)) {
          newErrors.bankAccount = bankDetls?.errMsg.item5;
          isValid = false;
        } else {
          newErrors.bankAccount = "";
        }
      } else {
        newErrors.bankAccount = "";
      }
    }

    if (!bankDetails.confirmAccount) {
      newErrors.confirmAccount = bankDetls?.errMsg.item6;
      isValid = false;
    } else {
      newErrors.confirmAccount = "";
    }

    if (!bankDetails.property) {
      newErrors.property = bankDetls?.errMsg.item7;
      isValid = false;
    } else {
      newErrors.property = "";
    }

    if (
      bankDetails.property === "no_i_own_property" &&
      !bankDetails.propertyAddress
    ) {
      newErrors.propertyAddress = bankDetls?.errMsg.item8;
      isValid = false;
    } else {
      newErrors.propertyAddress = "";
    }

    if (
      bankDetails.accountantContact === "Yes" &&
      (!bankDetails.accountantFirm || !bankDetails.accountantName)
    ) {
      newErrors.accountantFirm = bankDetls?.errMsg.item9;
      newErrors.accountantName = bankDetls?.errMsg.item10;
      isValid = false;
    } else {
      newErrors.accountantFirm = "";
      newErrors.accountantName = "";
    }

    if (!bankDetails.findFrom) {
      newErrors.findFrom = bankDetls?.errMsg.item11;
      isValid = false;
    } else {
      newErrors.findFrom = "";
    }

    if (bankDetails.findFrom === "fromOther" && !bankDetails.specify) {
      newErrors.specify = bankDetls?.errMsg.item12;
      isValid = false;
    } else {
      newErrors.specify = "";
    }

    setErrors(newErrors);

    if (isValid) {
      next();
    }
  };

  const fieldMapping = {
    isDirectDebitActive: "isDirectDebitActive",
    bankAccountType: "accountUuid",
    findFrom: "hearAboutUs",
    companyEntity: "organisationTypes",
    businessName: "legalName",
    confirmAccount: "isBankAccountConfirmed",
    bankAccount: "accountCode",
    specify: "hearAboutUsOther",
    accountantContact: "isAllowedContactAccountant",
    accountantFirm: "accountingFirmName",
    accountantName: "accountantName",
    property: "propertyOwnership",
    propertyAddress: "propertyOwnedAdddress"
  };

  // Fields that should update only onBlur
  const blurOnlyFields = [
    "businessName",
    "accountantFirm",
    "accountantName",
    "specify",
    "bankAccount",
    "propertyAddress"
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const rawValue = type === "checkbox" ? checked : value;
    const sanitizedValue =
      typeof rawValue === "string" ? sanitizeInput(rawValue) : rawValue;

    const newValue1 =
      type === "radio" ? sanitizedValue === "Yes" : sanitizedValue;

    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));

    let updatedDetails = {
      ...bankDetails,
      [name]: sanitizedValue,
      ...(name === "accountantFirm" || name === "accountantName"
        ? { accountantContact: true }
        : {})
    };

    if (name === "bankAccountType") {
      let selectedBank = null;
      let accountUuid = "";
      let accountCode = "";

      if (connectedSoftName === XERO) {
        selectedBank = bankName?.find(
          (acc) => acc?.AccountID === sanitizedValue
        );

        if (selectedBank) {
          accountUuid = selectedBank.AccountID;
          accountCode = selectedBank.BankAccountNumber;
        }
      }

      if (connectedSoftName === RECKON) {
        selectedBank = bankName?.find(
          (acc) => acc?.BankAccountID === sanitizedValue
        );

        if (selectedBank) {
          accountUuid = selectedBank.BankAccountID;
          accountCode = selectedBank.BankAccountNumber;
        }
      }

      if (connectedSoftName === MYOB || connectedSoftName === QUICKBOOKS) {
        selectedBank = bankName?.find(
          (acc) => acc?.accountUUID === sanitizedValue
        );

        if (selectedBank) {
          accountUuid = selectedBank.accountUUID;
          accountCode = selectedBank.accountNumber;
        }
      }

      if (selectedBank) {
        // Accounting software may format the account number with spaces or dashes
        // (e.g. Xero's NZ format "01-0123-0123456-00"), which breaks payment. Strip
        // them here while keeping accountUuid intact so writebacks keep working.
        // Some providers (e.g. Reckon) don't return a bank number at all here.
        accountCode = accountCode?.replace(/[\s-]/g, "");

        updatedDetails = {
          ...updatedDetails,
          bankAccountType: accountUuid,
          bankAccount: accountCode
        };

        setBankDetails(updatedDetails);

        handleUpdateOnboarding("bankAccount", {
          accountUuid,
          accountCode,
          isBankAccountConfirmed: bankDetails?.confirmAccount
        });

        return;
      }
    }

    setBankDetails(updatedDetails);

    if (!blurOnlyFields.includes(name) && name !== "bankAccountType") {
      const originalName = fieldMapping[name] || name;
      let finalValue = sanitizedValue;

      if (name === "accountantContact") {
        finalValue = newValue1;
      } else if (name === "accountantFirm" || name === "accountantName") {
        handleUpdateOnboarding("isAllowedContactAccountant", true);
      }

      handleUpdateOnboarding(originalName, finalValue);
    }

    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors, [name]: "" };

      if (name === "bankAccount" && value) {
        updatedErrors.bankAccountType = "";
      }

      if (name === "bankAccountType" && value) {
        updatedErrors.bankAccount = "";
      }

      return updatedErrors;
    });
  };

  const handleInputBlur = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value);

    const validateBankAccount = (value) => {
      if (!NUMBER_REGEX.test(value)) {
        return bankDetls.errMsg.item5;
      }

      if (country === COUNTRY_CODE_AU) {
        if (value.length < 7 || value.length > 15) {
          return customerTab?.directDebit?.auBankError;
        }
      } else if (country === COUNTRY_CODE_NZ) {
        if (value.length < 14 || value.length > 16) {
          return customerTab?.directDebit?.nzBankError;
        }
      }

      return "";
    };

    if (name === "bankAccount") {
      if (!value) return;
      const errorMsg = validateBankAccount(sanitizedValue);

      if (errorMsg) {
        setErrors((prev) => ({
          ...prev,
          bankAccount: errorMsg
        }));

        return;
      } else {
        setErrors((prev) => ({
          ...prev,
          bankAccount: ""
        }));
      }
    }

    if (blurOnlyFields.includes(name) && !errors.bankAccount) {
      const originalName = fieldMapping[name] || name;

      handleUpdateOnboarding(originalName, sanitizedValue);

      if (name === "accountantFirm" || name === "accountantName") {
        setBankDetails((prevDetails) => ({
          ...prevDetails,
          accountantContact: true
        }));
        handleUpdateOnboarding("isAllowedContactAccountant", true);
      }
    }
  };

  const authenticationUser = useSelector(
    (state) => state.userManagement?.profile
  );

  const handleSelectSuggestionAddress = (name, address) => {
    const sanitizedAddress = sanitizeInput(address);
    setBankDetails({ ...bankDetails, [name]: sanitizedAddress });
  };

  const getBankOptions = () => {
    if (!bankName) return [];

    switch (connectedSoftName) {
      case RECKON:
        return bankName.map((data) => ({
          value: data.BankAccountID,
          label: data.Description
        }));

      case MYOB: {
        const { bankAccount } = bankName;
        const accounts = Array.isArray(bankAccount)
          ? bankAccount
          : [bankAccount].filter(Boolean);

        return accounts.map((data) => ({
          value: data.accountUUID,
          label: `${data.accountName} - ${data.accountNumber}`
        }));
      }

      case XERO:
        return bankName.map((data) => ({
          value: data.AccountID,
          label: `${data.Name} - ${data.BankAccountNumber}`
        }));

      case QUICKBOOKS:
        return bankName.map((data) => ({
          value: data.accountUUID,
          label: data.accountName
        }));

      default:
        return [];
    }
  };

  const HIDE_MANUAL_INPUT = [XERO, MYOB, QUICKBOOKS];

  return (
    <>
      <div className="container">
        <div className="row">
          <div className="col-lg-8 offset-lg-2">
            <div className="step-wrap">
              <div className="step-title">
                <h3>{bankDetls.step2Title}</h3>
              </div>
              <div className="step-content bank-details-wrap">
                <div className="bank-details-form addowners-form">
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="bankAccRcv" className="py-2">
                        {bankDetls.bankAccRcv} <sup>*</sup>
                      </label>
                    </div>
                    <div className="col-md-6">
                      {bankName === "" || bankName === null ? (
                        <div className="input-wrap">
                          <input
                            type="text"
                            placeholder={createReffPartner?.bankAcc}
                            name="bankAccount"
                            value={bankDetails?.bankAccount || ""}
                            onChange={handleInputChange}
                            onBlur={handleInputBlur}
                          />
                          {errors.bankAccount && (
                            <div className="error-text mt-2 ">
                              {errors.bankAccount}
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          <div className="input-wrap">
                            <select
                              name="bankAccountType"
                              id="bankAccountType"
                              value={bankDetails?.bankAccountType || ""}
                              onChange={handleInputChange}
                            >
                              <option value="">
                                {customerTab?.directDebit.selectBankAcc}
                              </option>
                              {getBankOptions().map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            {errors.bankAccountType && (
                              <div className="error-text mt-2 ">
                                {errors.bankAccountType}
                              </div>
                            )}
                          </div>

                          {HIDE_MANUAL_INPUT.includes(connectedSoftName) && (
                            <div className="input-wrap mt-2">
                              <input
                                type="text"
                                placeholder={createReffPartner?.bankAcc}
                                name="bankAccount"
                                value={bankDetails?.bankAccount || ""}
                                onChange={handleInputChange}
                                onBlur={handleInputBlur}
                                disabled={!!bankDetails?.bankAccountType}
                              />
                              {errors.bankAccount && (
                                <div className="error-text mt-2 ">
                                  {errors.bankAccount}
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                      <div className="input-wrap mt-1">
                        <label htmlFor="letusKnow">
                          <small>{bankDetls.letusKnow}</small>
                        </label>
                      </div>
                      <div className="input-wrap">
                        <label htmlFor="confirmAccount" className="checkbox">
                          <input
                            type="checkbox"
                            name="confirmAccount"
                            id="confirmAccount"
                            checked={bankDetails.confirmAccount}
                            onChange={handleInputChange}
                          />
                          <span>{bankDetls.iConfirm}</span>
                        </label>
                        {errors.confirmAccount && (
                          <div className="error-text mt-2 ">
                            {errors.confirmAccount}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {bankName !== "" &&
                    bankName !== null &&
                    !HIDE_MANUAL_INPUT.includes(connectedSoftName) && (
                      <div className="row mb-2">
                        <div className="col-md-6">
                          <label htmlFor="bankAccNumber" className="py-2">
                            {bankDetls.bankAccNumber}
                            <sup>*</sup>
                          </label>
                        </div>
                        <div className="col-md-6">
                          <div className="input-wrap">
                            <input
                              type="text"
                              placeholder="Bank Account"
                              name="bankAccount"
                              value={bankDetails?.bankAccount || ""}
                              onChange={handleInputChange}
                              onBlur={handleInputBlur}
                            />
                            {errors.bankAccount && (
                              <div className="error-text mt-2 ">
                                {errors.bankAccount}
                              </div>
                            )}
                          </div>
                          <div className="input-wrap mt-1">
                            <label htmlFor="enterBank">
                              <small>{bankDetls.enterBank}</small>
                            </label>
                          </div>
                        </div>
                      </div>
                    )}
                  <div className="row mb-2">
                    <div className="col-md-6">
                      <label htmlFor="legalBsnsName" className="py-2">
                        {bankDetls.legalBsnsName} <sup>*</sup>
                      </label>
                    </div>
                    <div className="col-md-6">
                      <div className="input-wrap">
                        <input
                          name="businessName"
                          type="text"
                          placeholder="Legal Business Name"
                          value={bankDetails?.businessName || ""}
                          onChange={handleInputChange}
                          onBlur={handleInputBlur}
                        />
                        {errors.businessName && (
                          <div className="error-text mt-2 ">
                            {errors.businessName}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row mb-2">
                    <div className="col-md-6">
                      <label htmlFor="legalBsnsStructure" className="py-2">
                        {bankDetls.legalBsnsStructure} <sup>*</sup>
                      </label>
                    </div>
                    <div className="col-md-6">
                      <div className="input-wrap">
                        <select
                          name="companyEntity"
                          id="companyEntity"
                          value={bankDetails?.companyEntity || ""}
                          onChange={handleInputChange}
                        >
                          {companyEntityOptions()?.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>

                        {errors.companyEntity && (
                          <div className="error-text mt-2 ">
                            {errors.companyEntity}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row mb-2">
                    <div className="col-md-6">
                      <label htmlFor="canFundtap" className="py-2">
                        {bankDetls.canFundtap}
                      </label>
                    </div>
                    <div className="col-md-6">
                      <div className="input-wrap d-flex justify-content-end">
                        <span className="input-radio d-flex align-items-center">
                          <input
                            type="radio"
                            id="original_confirm_contact_authority1"
                            name="accountantContact"
                            onChange={handleInputChange}
                            value="Yes"
                            checked={
                              bankDetails?.accountantContact === true ||
                              bankDetails?.accountantContact === "Yes"
                            }
                          />
                          <label htmlFor="original_confirm_contact_authority1">
                            {createPricingPlan?.propertyOptions.item1}
                          </label>
                        </span>
                        <span className="input-radio ms-5 d-flex align-items-center">
                          <input
                            type="radio"
                            id="original_confirm_contact_authority2"
                            name="accountantContact"
                            onChange={handleInputChange}
                            value="No"
                            checked={
                              bankDetails?.accountantContact === false ||
                              bankDetails?.accountantContact === "No"
                            }
                          />
                          <label htmlFor="original_confirm_contact_authority2">
                            {createPricingPlan?.propertyOptions.item2}
                          </label>
                        </span>
                      </div>
                    </div>
                  </div>

                  {(bankDetails?.accountantContact === "Yes" ||
                    bankDetails?.accountantContact === true) && (
                    <>
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label htmlFor="accFirm" className="py-2">
                            {bankDetls.accFirm}
                          </label>
                        </div>
                        <div className="col-md-6">
                          <div className="input-wrap">
                            <input
                              type="text"
                              placeholder={bankDetls.accFirm}
                              name="accountantFirm"
                              value={bankDetails?.accountantFirm || ""}
                              onChange={handleInputChange}
                              onBlur={handleInputBlur}
                            />
                            {errors.accountantFirm && (
                              <div className="error-text mt-2 ">
                                {errors.accountantFirm}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label htmlFor="accName" className="py-2">
                            {customerTab?.marketing.accName}
                          </label>
                        </div>
                        <div className="col-md-6">
                          <div className="input-wrap">
                            <input
                              type="text"
                              placeholder={customerTab?.marketing.accName}
                              name="accountantName"
                              value={bankDetails?.accountantName || ""}
                              onChange={handleInputChange}
                              onBlur={handleInputBlur}
                            />
                            {errors.accountantName && (
                              <div className="error-text mt-2 ">
                                {errors.accountantName}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="row mb-2">
                    <div className="col-md-6">
                      <label htmlFor="doYouOwn" className="py-2">
                        {bankDetls.doYouOwn} <sup>*</sup>
                      </label>
                    </div>
                    <div className="col-md-6">
                      <div className="input-wrap">
                        <select
                          id="property"
                          name="property"
                          value={bankDetails.property || ""}
                          onChange={handleInputChange}
                        >
                          {getOwnPropertyOptions()?.map((item, index) => (
                            <option value={item.value} key={index}>
                              {item.label}
                            </option>
                          ))}
                        </select>
                        {errors.property && (
                          <div className="error-text mt-2 ">
                            {errors.property}
                          </div>
                        )}
                      </div>
                      <div className="input-wrap mt-1">
                        <label htmlFor="doYouOwn2">
                          <small>{bankDetls.doYouOwn2}</small>
                        </label>
                      </div>
                    </div>
                  </div>

                  {bankDetails?.property === "No-but I own other Property" && (
                    <div className="row mb-2">
                      <div className="col-md-6">
                        <label htmlFor="" className="py-2">
                          {ownerTab?.proprtyDetails.propertyOwnedAddrs}
                          <sup>*</sup>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <div className="input-wrap">
                          <WidgetInput
                            addressFinderKey={ADDRESSFINDERKEY}
                            id="propertyAddress"
                            value={bankDetails?.propertyAddress || ""}
                            country={
                              authenticationUser?.userAccountMeta
                                ?.primaryCountry
                            }
                            name="propertyAddress"
                            onSelected={(address) => {
                              handleSelectSuggestionAddress(
                                "propertyAddress",
                                address
                              );
                            }}
                            onChange={handleInputChange}
                            onBlur={handleInputBlur}
                          />
                          {errors.propertyAddress && (
                            <div className="error-text mt-2 ">
                              {errors.propertyAddress}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="row mb-2">
                    <div className="col-md-6">
                      <label htmlFor="" className="py-2">
                        {bankDetls.howYouHear} <sup>*</sup>
                      </label>
                    </div>
                    <div className="col-md-6">
                      <div className="input-wrap">
                        <select
                          id="findFrom"
                          name="findFrom"
                          value={bankDetails?.findFrom || ""}
                          onChange={handleInputChange}
                        >
                          {getFindFromOptions()?.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>

                        {errors.findFrom && (
                          <div className="error-text mt-2 ">
                            {errors.findFrom}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {bankDetails?.findFrom === "fromOther" && (
                    <div className="row mb-2">
                      <div className="col-md-6">
                        <label htmlFor="" className="py-2">
                          {bankDetls?.pleaseSpecify} <sup>*</sup>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <div className="input-wrap">
                          <input
                            type="text"
                            placeholder="Please specify"
                            name="specify"
                            value={bankDetails?.specify || ""}
                            onChange={handleInputChange}
                            onBlur={handleInputBlur}
                          />
                          {errors.specify && (
                            <div className="error-text mt-2 ">
                              {errors.specify}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="step-btn-group d-flex justify-content-center align-items-center mt-5 mb-5 flex-wrap">
        <div className="prev-btn p-1">
          <button type="button" className="btn btn-primary" onClick={prev}>
            {pagination.previous}
          </button>
        </div>
        <div className="next-btn p-1">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
          >
            {pagination.next}
          </button>
        </div>
      </div>
    </>
  );
};

export default BankDetails;
